import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import type { PickerItemValue } from "@design-system/Picker/Picker.types";
import type { PickerListRootProps } from "@design-system/Picker/PickerRoot.types";

export type PickerLoadMoreProps = Required<
    Pick<
        PickerListRootProps<PickerItemValue>,
        "hasMore" | "onLoadMore" | "loadMoreStatus"
    >
>;

/**
 * Maps a react-query infinite query onto `Picker.Select`'s paging props:
 * `hasMore`, `onLoadMore` and `loadMoreStatus`. Spread the result on the root.
 */
export const toPickerLoadMore = <TData, TError>(
    query: Pick<
        UseInfiniteQueryResult<TData, TError>,
        | "hasNextPage"
        | "fetchNextPage"
        | "isFetchingNextPage"
        | "isFetchNextPageError"
    >,
): PickerLoadMoreProps => ({
    hasMore: query.hasNextPage,
    onLoadMore: () => void query.fetchNextPage(),
    loadMoreStatus: getLoadMoreStatus(query),
});

const getLoadMoreStatus = ({
    isFetchingNextPage,
    isFetchNextPageError,
}: {
    isFetchingNextPage: boolean;
    isFetchNextPageError: boolean;
}): PickerLoadMoreProps["loadMoreStatus"] => {
    if (isFetchingNextPage) {
        return "loading";
    }
    return isFetchNextPageError ? "error" : "idle";
};
